import type { BlogPost, BlogAuthor } from '@myTypes/blog';

// Import all markdown files
const blogFiles = import.meta.glob('@content/blog/*.md', { as: 'raw', eager: true });

const defaultAuthor: BlogAuthor = {
  name: 'Richard Midwinter',
  role: 'CTO',
  image: '/images/team/cto.jpg',
  linkedin: 'https://www.linkedin.com/in/r-i-c-h-a-r-d',
};

function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

function isFutureDate(dateStr: string): boolean {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // Normalise input date to handle single-digit months/days (e.g. '2026-05-6')
  const parts = dateStr.split('-');
  if (parts.length !== 3) return false;
  const normalised = `${parts[0]}-${String(parts[1]).padStart(2, '0')}-${String(parts[2]).padStart(2, '0')}`;

  return normalised > today;
}

function parseFrontmatter(content: string): { frontmatter: Record<string, string>; body: string } {
  const match = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);

  if (!match) {
    return { frontmatter: {}, body: content };
  }

  const frontmatter: Record<string, string> = {};
  const frontmatterContent = match[1];
  const body = match[2].trim();

  frontmatterContent.split('\n').forEach(line => {
    const [key, ...valueParts] = line.split(':');
    if (key && valueParts.length > 0) {
      const value = valueParts
        .join(':')
        .trim()
        .replace(/^["']|["']$/g, '');
      frontmatter[key.trim()] = value;
    }
  });

  return { frontmatter, body };
}

export function getAllBlogPosts(): BlogPost[] {
  const posts: BlogPost[] = [];

  for (const [path, content] of Object.entries(blogFiles)) {
    const slug = path.replace(/^.*\//, '').replace(/\.md$/, '');
    const { frontmatter, body } = parseFrontmatter(content as string);

    posts.push({
      slug,
      title: frontmatter.title || '',
      date: frontmatter.date || '',
      readTime: frontmatter.readTime || '',
      category: frontmatter.category || '',
      excerpt: frontmatter.excerpt || '',
      image: frontmatter.image || undefined,
      content: body,
      author: defaultAuthor,
    });
  }

  // Filter out future-dated posts unless on localhost
  const visiblePosts = isLocalhost() ? posts : posts.filter(post => !isFutureDate(post.date));

  // Sort by date, newest first
  return visiblePosts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function getBlogPostBySlug(slug: string): BlogPost | undefined {
  return getAllBlogPosts().find(post => post.slug === slug);
}

export function getLatestBlogPosts(limit: number = 3): BlogPost[] {
  return getAllBlogPosts().slice(0, limit);
}
